//HomePage.js

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Carousel, Modal, Button, Badge } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "./HomePage.css";
import LoadingComponent from "./LoadingComponent";
import EnrollmentDetailsModal from "./EnrollmentDetailsModal";

const HomePage = ({ trainerID, isAdmin, isManager }) => {
	const [data, setData] = useState([]);
	const [groupedData, setGroupedData] = useState({});
	const [isLoading, setIsLoading] = useState(true);
	const [dataError, setDataError] = useState(null);

	const [searchTerm, setSearchTerm] = useState("");
	const [filterSite, setFilterSite] = useState("");

	const [awaitingTrainingCount, setAwaitingTrainingCount] = useState(null);
	const [completedTrainingCount, setCompletedTrainingCount] = useState(null);
	const [trainingStartedCount, setTrainingStartedCount] = useState(null);
	const [completedEnrollmentCount, setCompletedEnrollmentCount] = useState(null);

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [modalData, setModalData] = useState([]);

	const [documentUrls, setDocumentUrls] = useState([]);
	const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
	const [selectedDocument, setSelectedDocument] = useState(null);

	const theme = document.body.getAttribute("data-theme") || "light";

	const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

	const fetchWithTimeout = async (url, options = {}, timeoutMs = 15000) => {
		const controller = options.signal ? null : new AbortController();
		const signal = options.signal || controller.signal;

		const timeoutId = setTimeout(() => {
			try {
				controller && controller.abort();
			} catch (_) {}
		}, timeoutMs);

		try {
			const res = await fetch(url, { ...options, signal });
			return res;
		} finally {
			clearTimeout(timeoutId);
		}
	};

	const fetchWithRetry = async (
		url,
		options = {},
		{
			retries = 2,
			timeoutMs = 15000,
			baseDelayMs = 400,
			backoff = 2,
			jitterMs = 250,
		} = {},
	) => {
		let attempt = 0;
		let lastErr = null;

		while (attempt <= retries) {
			try {
				const res = await fetchWithTimeout(url, options, timeoutMs);

				if ([408, 429, 500, 502, 503, 504].includes(res.status)) {
					if (attempt === retries) return res;
					const delay =
						baseDelayMs * Math.pow(backoff, attempt) +
						Math.floor(Math.random() * jitterMs);
					await sleep(delay);
					attempt++;
					continue;
				}

				return res;
			} catch (err) {
				if (err?.name === "AbortError") throw err;

				lastErr = err;
				if (attempt === retries) throw lastErr;

				const delay =
					baseDelayMs * Math.pow(backoff, attempt) +
					Math.floor(Math.random() * jitterMs);
				await sleep(delay);
				attempt++;
			}
		}

		throw lastErr || new Error("Unknown fetch error");
	};

	const fetchJson = async (url, options = {}, retryOpts = {}) => {
		const res = await fetchWithRetry(url, options, retryOpts);
		const ct = res.headers.get("content-type") || "";
		const isJson = ct.includes("application/json");
		const body = isJson ? await res.json() : await res.text();

		if (!res.ok) {
			const msg =
				typeof body === "string" ? body : body?.message || JSON.stringify(body);
			throw new Error(`HTTP ${res.status} ${res.statusText} - ${msg}`);
		}

		return body;
	};

	const authHeaders = {
		"x-trainer-id": trainerID,
	};

	const fetchData = async (signal) => {
		const api = process.env.REACT_APP_API_URL;
		if (!api) throw new Error("REACT_APP_API_URL is not set");

		const elevated = Boolean(isAdmin || isManager);
		if (elevated) {
			const url = `${api}/permissions/trainers/${encodeURIComponent(
				trainerID,
			)}`;

			const rows = await fetchJson(
				url,
				{
					signal,
					headers: authHeaders,
				},
				{ retries: 2, timeoutMs: 20000 },
			);

			const trainers = Array.isArray(rows) ? rows : [];
			const sites = trainers
				.filter((t) => String(t?.etp_alias || "").trim() !== "")
				.map((t) => ({
					etp_mstr_alias: t.etp_alias,
					etp_mstr_wd_name: t.user_name || t.TrainerID || "",
					etp_mstr_site: t.SiteID || "",
					active_sts: "true",
				}));

			setData(sites);
			const grouped = sites.reduce((acc, item) => {
				const alias = item.etp_mstr_alias;
				(acc[alias] ||= []).push(item);
				return acc;
			}, {});
			setGroupedData(grouped);

			setAwaitingTrainingCount(null);
			setCompletedTrainingCount(null);
			setTrainingStartedCount(null);
			setCompletedEnrollmentCount(null);
			return;
		}

		const url = `${api}/permissions/${encodeURIComponent(trainerID)}/sites`;

		const fetchedData = await fetchJson(
			url,
			{
				signal,
				headers: authHeaders,
			},
			{ retries: 2, timeoutMs: 60000 },
		);

		const sites = fetchedData.sites || [];
		setData(sites);

		const grouped = sites.reduce((acc, item) => {
			const alias = item.etp_mstr_alias;
			(acc[alias] ||= []).push(item);
			return acc;
		}, {});
		setGroupedData(grouped);

		if (fetchedData.counts) {
			setAwaitingTrainingCount(fetchedData.counts.awaitingTraining || 0);
			setCompletedTrainingCount(fetchedData.counts.completedTraining || 0);
			setTrainingStartedCount(fetchedData.counts.trainingStarted || 0);
		}
	};

	const fetchEnrollmentDetails = async (signal) => {
		const api = process.env.REACT_APP_API_URL;
		if (!api) throw new Error("REACT_APP_API_URL is not set");

		if (isAdmin || isManager) {
			setCompletedEnrollmentCount(null);
			setModalData([]);
			return;
		}

		const url = `${api}/permissions/completed-enrollments/${encodeURIComponent(
			trainerID,
		)}`;

		const rows = await fetchJson(
			url,
			{
				signal,
				headers: authHeaders,
			},
			{ retries: 2, timeoutMs: 30000 },
		);

		setModalData(Array.isArray(rows) ? rows : []);
			setCompletedEnrollmentCount(Array.isArray(rows) ? rows.length : 0);
	};

	const fetchDocuments = async (signal) => {
		const api = process.env.REACT_APP_API_URL;
		if (!api) throw new Error("REACT_APP_API_URL is not set");

		const url = `${api}/documents`;

		const docs = await fetchJson(
			url,
			{
				signal,
				headers: authHeaders,
			},
			{ retries: 2, timeoutMs: 20000 },
		);

		setDocumentUrls(Array.isArray(docs) ? docs : []);
	};

	useEffect(() => {
		let isMounted = true;
		const controller = new AbortController();

		if (trainerID) {
			setIsLoading(true);
			setDataError(null);

			(async () => {
				try {
					await fetchData(controller.signal);
				} catch (err) {
					if (isMounted) {
						setDataError(
							err?.message ||
								"Unable to load trainees right now. Please retry.",
						);
						setData([]);
						setGroupedData({});
					}
				} finally {
					// Non-blocking: enrollment + docs should not hide the page
					await Promise.allSettled([
						fetchEnrollmentDetails(controller.signal),
						fetchDocuments(controller.signal),
					]);
					if (isMounted) setIsLoading(false);
				}
			})();
		}

		return () => {
			isMounted = false;
			try {
				controller.abort();
			} catch (_) {}
		};
	}, [trainerID, isAdmin, isManager]);

	const handleCardClick = () => {
		setIsModalOpen(true);
	};

	const handleThumbnailClick = (document) => {
		setSelectedDocument(document);
		setIsDocumentModalOpen(true);
	};

	const handleCloseModal = () => setIsModalOpen(false);

	const handleCloseDocumentModal = () => {
		setIsDocumentModalOpen(false);
		setSelectedDocument(null);
	};

	const filteredGroupedAliases = useMemo(() => {
		const term = searchTerm.trim().toLowerCase();
		return Object.keys(groupedData).filter((alias) => {
			return groupedData[alias].some((item) => {
				if (item.active_sts !== "true") return false;

				const matchesTerm =
					!term ||
					item.etp_mstr_alias?.toLowerCase().includes(term) ||
					item.etp_mstr_wd_name?.toLowerCase().includes(term);

				const matchesSite = !filterSite || item.etp_mstr_site === filterSite;

				return matchesTerm && matchesSite;
			});
		});
	}, [groupedData, searchTerm, filterSite]);

	const uniqueSites = useMemo(() => {
		return [...new Set((data || []).map((item) => item.etp_mstr_site))].filter(
			Boolean,
		);
	}, [data]);

	if (isLoading) return <LoadingComponent />;

	return (
		<div className="hp">
			<section className="hp__panel">
				<div className="hp__panelHeader">
					<h2 className="hp__title">Information Center</h2>
					<div className="hp__subtitle">
						Click a slide to open the full document.
					</div>
				</div>

				<div className="hp__carousel">
					<Carousel interval={4500}>
						{documentUrls.map((doc, index) => (
							<Carousel.Item
								key={index}
								onClick={() => handleThumbnailClick(doc)}
							>
								<div className="hp__thumbWrap">
									<img
										className="d-block w-100"
										src={doc.thumbnail}
										alt={`Slide ${index}`}
									/>
								</div>
							</Carousel.Item>
						))}
					</Carousel>
				</div>
			</section>

			<section className="hp__panel">
				<div className="hp__topbar">
					<div className="hp__brand">
						<h1 className="hp__brandTitle">Tech Dev</h1>
					</div>

					{completedEnrollmentCount !== null &&
						trainingStartedCount !== null &&
						completedTrainingCount !== null && (
							<div className="hp__stats">
								<div className="hp__stat">
									<span className="hp__statLabel">
										Workday Enrollments Completed
									</span>
									<Badge bg="secondary" pill className="hp__statValue">
										{completedEnrollmentCount}
									</Badge>
								</div>

								<div className="hp__stat">
									<span className="hp__statLabel">CC Initiated</span>
									<Badge bg="secondary" pill className="hp__statValue">
										{trainingStartedCount}
									</Badge>
								</div>

								<div className="hp__stat">
									<span className="hp__statLabel">CC Completed</span>
									<Badge bg="secondary" pill className="hp__statValue">
										{completedTrainingCount}
									</Badge>
								</div>
							</div>
						)}
				</div>

				<div className="hp__filters">
					<div className="hp__filtersLeft">
						<div className="hp__label">Your trainees</div>
						<div className="hp__count">
							Showing <strong>{filteredGroupedAliases.length}</strong>
						</div>
					</div>

					<div className="hp__filtersRight">
						<input
							className="hp__input"
							type="text"
							placeholder="Search by name or alias"
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
						/>

						<select
							className="hp__select"
							value={filterSite}
							onChange={(e) => setFilterSite(e.target.value)}
						>
							<option value="">All Sites</option>
							{uniqueSites.map((site) => (
								<option key={site} value={site}>
									{site}
								</option>
							))}
						</select>
					</div>
				</div>

				<div className="hp__tableWrap">
					{dataError && (
						<div className="alert alert-danger mb-3" role="alert">
							{dataError}
						</div>
					)}
					<table className="table table-hover hp__table">
						<thead>
							<tr>
								<th>Alias</th>
								<th>Name</th>
								<th>Site</th>
								<th className="hp__actionsCol">Actions</th>
							</tr>
						</thead>
						<tbody>
							{filteredGroupedAliases.length > 0 ? (
								filteredGroupedAliases.map((alias, idx) => {
									const firstItem = groupedData[alias]?.[0];
									if (!firstItem) return null;

									const bgColor =
										theme === "dark"
											? idx % 2 === 0
												? "#2c2c2c"
												: "#1a1a1a"
											: undefined;

									return (
										<tr key={alias} style={{ backgroundColor: bgColor }}>
											<td className="hp__mono">{alias}</td>
											<td>{firstItem.etp_mstr_wd_name}</td>
											<td>{firstItem.etp_mstr_site}</td>
											<td className="hp__actionsCol">
												<Link
													className="btn btn-primary btn-sm"
													to={`/details/${firstItem.etp_mstr_alias}`}
													onClick={() =>
														console.log("Details click", {
															alias: firstItem.etp_mstr_alias,
															name: firstItem.etp_mstr_wd_name,
															site: firstItem.etp_mstr_site,
														})
													}
												>
													Details
												</Link>
											</td>
										</tr>
									);
								})
							) : (
								<tr>
									<td colSpan={4} className="text-center py-4">
										{dataError
											? "Unable to load trainees."
											: "No trainees found."}
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</section>

			{completedEnrollmentCount !== null && (
				<EnrollmentDetailsModal
					isOpen={isModalOpen}
					onClose={handleCloseModal}
					data={modalData}
				/>
			)}

			<Modal
				className="document-modal"
				show={isDocumentModalOpen}
				onHide={handleCloseDocumentModal}
				size="xl"
			>
				<Modal.Header closeButton>
					<Modal.Title>Document Viewer</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{selectedDocument && (
						<iframe
							src={selectedDocument.url}
							className="document-iframe"
							title="Document Viewer"
							sandbox="allow-scripts allow-same-origin"
						/>
					)}
				</Modal.Body>
				<Modal.Footer>
					<Button variant="secondary" onClick={handleCloseDocumentModal}>
						Close
					</Button>
				</Modal.Footer>
			</Modal>
		</div>
	);
};

export default HomePage;
